import Image from 'next/image'
import React, { Dispatch, SetStateAction } from 'react'

import { zodResolver } from '@hookform/resolvers/zod'
import clsx from 'clsx'
import { useForm } from 'react-hook-form'
import { FormattedMessage, useIntl } from 'react-intl'

import { updateNotificationEmail } from '@/lib/api/users'
import { EmailData, EmailDataSchema } from '@/lib/schemes/auth'

import useLoading from '@/hooks/useLoading'

import Button from '@/components/Button'
import Input from '@/components/Input'
import { Dialog, SpecificDialogProps } from '@/components/dialogs/Dialog'
import Form from '@/components/form/Form'
import FormField from '@/components/form/FormField'

interface SignInDialogProps extends SpecificDialogProps {
  isUpdateNotificationEmail?: boolean
  hasCloseIcon?: boolean
  openConfirmEmailModal: () => void
  setEmail: Dispatch<SetStateAction<string>>
}

export const NewEmailDialog = ({
  isOpen,
  hasCloseIcon,
  isUpdateNotificationEmail,
  onClose,
  setEmail,
  openConfirmEmailModal
}: SignInDialogProps) => {
  const { formatMessage } = useIntl()
  const { isLoading, tryLoad } = useLoading()

  const {
    reset,
    clearErrors,
    handleSubmit,
    register,
    formState: { errors }
  } = useForm<EmailData>({
    resolver: zodResolver(EmailDataSchema)
  })

  const handleCloseModal = () => {
    reset()
    clearErrors()
    onClose && setTimeout(onClose, 0)
  }

  const onSubmit = async ({ email }: EmailData) => {
    await tryLoad(async () => {
      await updateNotificationEmail({ email })

      setEmail(email)
      openConfirmEmailModal()
      setTimeout(handleCloseModal, 0)
    })
  }

  return (
    <Dialog
      {...{
        isOpen,
        hideCloseIcon: !hasCloseIcon,
        onClose: hasCloseIcon ? handleCloseModal : () => null,
        hasTitle: hasCloseIcon || isUpdateNotificationEmail,
        ...(isUpdateNotificationEmail && {
          title: formatMessage({
            id: 'dialog.email.title',
            defaultMessage: 'Новый email для уведомлений'
          })
        }),
        className: 'tablet:!min-w-[400px]'
      }}
    >
      <div
        className={clsx(
          'flex flex-col gap-6',
          !isUpdateNotificationEmail && 'mt-6'
        )}
      >
        <Form
          className="justify-center gap-4"
          onSubmit={handleSubmit(onSubmit)}
        >
          <div className="mb-4 flex h-48 justify-center">
            <Image
              className="h-full w-auto"
              alt="confirm"
              height={160}
              src="/images/dog-airplane.svg"
              width={106}
            />
          </div>
          {!isUpdateNotificationEmail && (
            <div className="flex flex-col gap-6 text-center">
              <div className="text-2xl font-medium">
                <FormattedMessage
                  {...{
                    id: 'dialog.email.text',
                    defaultMessage: 'Введите email-адрес'
                  }}
                />
              </div>
              <div>
                <FormattedMessage
                  {...{
                    id: 'dialog.email.subtext',
                    defaultMessage:
                      'На него мы будем отправлять только информацию о балансе и важные уведомления'
                  }}
                />
              </div>
            </div>
          )}
          <FormField error={errors.email}>
            <Input
              inputMode="email"
              placeholder="Email"
              {...register('email')}
            />
          </FormField>
          <Button
            {...{
              isLoading,
              variant: 'secondary',
              className: 'border-[0.5px]'
            }}
          >
            <FormattedMessage
              {...{
                id: 'button.continue',
                defaultMessage: 'Продолжить'
              }}
            />
          </Button>
        </Form>
      </div>
    </Dialog>
  )
}
